import React, { useState, useEffect } from 'react';
import {
  Box, Flex, HStack, Text, Table, Thead, Tbody, Tr, Th, Td, Badge,
  IconButton, Select, Input, Button, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalCloseButton, ModalBody, Spinner, Slider, SliderTrack,
  SliderFilledTrack, SliderThumb, Heading, SimpleGrid,
  useToast, Divider, Icon, Image
} from '@chakra-ui/react';
import { FiRefreshCw, FiEye, FiSearch, FiShield } from 'react-icons/fi';
import SaktiSidebar from '../components/SaktiSidebar';
import SaktiApiService from '../services/saktiApiService';
import SaktiAttendanceAuditApiService from '../services/saktiAttendanceAuditApiService';
import { useSaktiLivenessAudit } from '../hooks/useSaktiLivenessAudit';
import { SaktiFilterOptions, SaktiLivenessAuditItem } from '../types';
import { SAKTI_UI_SIZE } from '../uiSizes';

const getTodayDate = () => new Date().toISOString().split('T')[0];

const SaktiLivenessAuditPage: React.FC = () => {
  const toast = useToast();
  const [options, setOptions] = useState<SaktiFilterOptions>({ companies: [], estates: [], divisions: [] });
  
  // Local filter states
  const [selectedCompany, setSelectedCompany] = useState<number>(1);
  const [selectedEstate, setSelectedEstate] = useState<number | undefined>();
  const [selectedDivision, setSelectedDivision] = useState<number | undefined>();
  const [selectedDate, setSelectedDate] = useState<string>(getTodayDate());
  const [verdictFilter, setVerdictFilter] = useState<'all' | 'live' | 'spoof' | 'unaudited'>('all');
  const [overrideFilter, setOverrideFilter] = useState<'all' | 'true' | 'false'>('all');
  const [searchVal, setSearchVal] = useState<string>('');

  const { audits, loading, setFilters, refetch } = useSaktiLivenessAudit({
    date: selectedDate,
    verdict: verdictFilter,
    search: '',
  });

  // Modal states
  const [selectedItem, setSelectedItem] = useState<SaktiLivenessAuditItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [originalBlobUrl, setOriginalBlobUrl] = useState<string>('');
  const [heatmapBlobUrl, setHeatmapBlobUrl] = useState<string>('');
  const [imageLoading, setImageLoading] = useState(false);
  const [overlayOpacity, setOverlayOpacity] = useState<number>(0.4);

  useEffect(() => {
    async function loadOptions() {
      try {
        const fetched = await SaktiApiService.fetchFilterOptions();
        setOptions(fetched);
        if (fetched.companies.length > 0) setSelectedCompany(fetched.companies[0].id);
      } catch (err) {
        toast({ title: 'Gagal memuat filter', status: 'error', duration: 4000 });
      }
    }
    loadOptions();
  }, [toast]);

  // Auto-apply date and dropdown filters when they change
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      company_id: selectedCompany,
      estate_id: selectedEstate,
      division_id: selectedDivision,
      date: selectedDate,
      verdict: verdictFilter,
      is_override: overrideFilter === 'all' ? undefined : overrideFilter === 'true',
    }));
  }, [selectedCompany, selectedEstate, selectedDivision, selectedDate, verdictFilter, overrideFilter, setFilters]);

  const handleApplyFilters = () => {
    setFilters({
      company_id: selectedCompany,
      estate_id: selectedEstate,
      division_id: selectedDivision,
      date: selectedDate,
      verdict: verdictFilter,
      is_override: overrideFilter === 'all' ? undefined : overrideFilter === 'true',
      search: searchVal,
    });
  };

  const handleOpenDetail = async (item: SaktiLivenessAuditItem) => {
    setSelectedItem(item);
    setIsModalOpen(true);
    setImageLoading(true);
    setOriginalBlobUrl('');
    setHeatmapBlobUrl('');
    
    try {
      const origUrl = await SaktiAttendanceAuditApiService.getImageBlobUrl(item.pic_path);
      setOriginalBlobUrl(origUrl);
      
      if (item.heatmap_path) {
        const heatUrl = await SaktiAttendanceAuditApiService.getImageBlobUrl(item.heatmap_path);
        setHeatmapBlobUrl(heatUrl);
      }
    } catch (err) {
      toast({ title: 'Gagal memuat foto audit', status: 'warning', duration: 3000 });
    } finally {
      setImageLoading(false);
    }
  };

  const handleCloseDetail = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    if (originalBlobUrl) URL.revokeObjectURL(originalBlobUrl);
    if (heatmapBlobUrl) URL.revokeObjectURL(heatmapBlobUrl);
  };

  const getScoreColor = (score?: number) => {
    if (score === undefined) return 'gray';
    if (score >= 0.5) return 'red';
    if (score >= 0.3) return 'yellow';
    return 'green';
  };

  return (
    <Box minH="100vh" bg="#ECEEF1">
      <Flex align="stretch">
        <Box display={{ base: 'none', xl: 'block' }}>
          <SaktiSidebar activeMenu="attendance-audit" />
        </Box>

        <Box flex={1} p={SAKTI_UI_SIZE.pagePadding}>
          <Heading size="lg" mb={5}>Face Verification Anti-Spoofing Audit</Heading>
          
          {/* Filters card */}
          <Box bg="white" p={5} borderRadius="xl" shadow="sm" mb={5}>
            <SimpleGrid columns={{ base: 1, md: 3, lg: 7 }} gap={4} alignItems="end">
              <Box>
                <Text fontSize="xs" fontWeight="semibold" mb={1} color="gray.500">TANGGAL RKH</Text>
                <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} size="sm" />
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="semibold" mb={1} color="gray.500">ESTATE</Text>
                <Select placeholder="Semua Estate" size="sm" onChange={(e) => setSelectedEstate(e.target.value ? Number(e.target.value) : undefined)}>
                  {options.estates.map(es => <option key={es.id} value={es.odoo_id}>{es.name}</option>)}
                </Select>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="semibold" mb={1} color="gray.500">DIVISI</Text>
                <Select placeholder="Semua Divisi" size="sm" onChange={(e) => setSelectedDivision(e.target.value ? Number(e.target.value) : undefined)}>
                  {options.divisions
                    .filter(d => {
                      if (!selectedEstate) return true;
                      const matchedEstate = options.estates.find(es => es.odoo_id === selectedEstate);
                      return matchedEstate ? d.estate_id === matchedEstate.id : false;
                    })
                    .map(div => <option key={div.id} value={div.odoo_id}>{div.name}</option>)
                  }
                </Select>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="semibold" mb={1} color="gray.500">VERDIKSI MODEL</Text>
                <Select size="sm" value={verdictFilter} onChange={(e: any) => setVerdictFilter(e.target.value)}>
                  <option value="all">Semua Hasil</option>
                  <option value="spoof">Spoof (Serangan)</option>
                  <option value="live">Live (Asli)</option>
                  <option value="unaudited">Belum Diaudit</option>
                </Select>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="semibold" mb={1} color="gray.500">BYPASS / OVERRIDE</Text>
                <Select size="sm" value={overrideFilter} onChange={(e: any) => setOverrideFilter(e.target.value)}>
                  <option value="all">Semua Status</option>
                  <option value="true">Bypass (TOTP)</option>
                  <option value="false">Verifikasi Normal</option>
                </Select>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="semibold" mb={1} color="gray.500">CARI PEMANEN</Text>
                <Input placeholder="Nama / NIP" size="sm" value={searchVal} onChange={(e) => setSearchVal(e.target.value)} />
              </Box>
              <Button size="sm" colorScheme="teal" leftIcon={<FiSearch />} onClick={handleApplyFilters}>Terapkan</Button>
            </SimpleGrid>
          </Box>

          {/* Audit log Table */}
          <Box bg="white" borderRadius="xl" shadow="sm" overflow="hidden">
            <Flex justify="space-between" align="center" p={4} borderBottom="1px" borderColor="gray.100">
              <Text fontWeight="bold">Daftar Kehadiran & Liveness Audit ({audits.length})</Text>
              <IconButton aria-label="refresh" icon={<FiRefreshCw />} size="sm" onClick={refetch} isLoading={loading} />
            </Flex>

            {loading ? (
              <Flex py={10} justify="center"><Spinner size="xl" color="teal.500" /></Flex>
            ) : (
              <Table variant="simple" size="sm">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Pemanen</Th>
                    <Th>Estate / Divisi</Th>
                    <Th>Waktu Foto</Th>
                    <Th>Tipe</Th>
                    <Th textAlign="center">Bypass</Th>
                    <Th textAlign="center">Spoof Score</Th>
                    <Th textAlign="center">Hasil</Th>
                    <Th textAlign="center">Aksi</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {audits.map(item => (
                    <Tr key={item.capture_id}>
                      <Td>
                        <Box>
                          <Text fontWeight="bold">{item.name}</Text>
                          <Text fontSize="xs" color="gray.500">{item.nip}</Text>
                        </Box>
                      </Td>
                      <Td>{item.estate_name} - {item.division_name}</Td>
                      <Td>{item.captured_at ? new Date(item.captured_at).toLocaleTimeString('id-ID') : '-'}</Td>
                      <Td><Badge>{item.capture_kind === 'in' ? 'CLOCK IN' : 'CLOCK OUT'}</Badge></Td>
                      <Td textAlign="center">
                        {item.is_override ? (
                          <Badge colorScheme="purple"><Icon as={FiShield} mr={1} />TOTP BYPASS</Badge>
                        ) : <Text color="gray.300">-</Text>}
                      </Td>
                      <Td textAlign="center">
                        <Badge colorScheme={getScoreColor(item.spoof_score)}>
                          {item.spoof_score !== undefined ? item.spoof_score.toFixed(4) : 'PENDING'}
                        </Badge>
                      </Td>
                      <Td textAlign="center">
                        <Badge colorScheme={item.verdict === 'spoof' ? 'red' : 'green'}>
                          {item.verdict ? item.verdict.toUpperCase() : 'WAITING'}
                        </Badge>
                      </Td>
                      <Td textAlign="center">
                        <IconButton aria-label="View Details" icon={<FiEye />} size="xs" colorScheme="blue" variant="ghost" onClick={() => handleOpenDetail(item)} />
                      </Td>
                    </Tr>
                  ))}
                  {audits.length === 0 && (
                    <Tr><Td colSpan={8} textAlign="center" py={10} color="gray.400">Tidak ada log kecocokan audit untuk filter saat ini</Td></Tr>
                  )}
                </Tbody>
              </Table>
            )}
          </Box>
        </Box>
      </Flex>

      {/* Opacity Blended Heatmap Overlay Modal */}
      <Modal isOpen={isModalOpen} onClose={handleCloseDetail} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Audit Wajah & Deteksi Presentation Attack</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedItem && (
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} mb={5}>
                <Box>
                  <Text fontSize="xs" color="gray.500">NAMA KARYAWAN</Text>
                  <Text fontWeight="bold">{selectedItem.name} ({selectedItem.nip})</Text>
                  <Text fontSize="xs" color="gray.500" mt={2}>FOREMAN / MANDOR</Text>
                  <Text fontWeight="semibold" fontSize="sm">{selectedItem.foreman_name || '-'}</Text>
                  <Text fontSize="xs" color="gray.500">{selectedItem.foreman_group_name || '-'}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">SCORE DETEKSI</Text>
                  <HStack spacing={2} mt={1}>
                    <Badge colorScheme={getScoreColor(selectedItem.spoof_score)} fontSize="md" px={2}>
                      {selectedItem.spoof_score !== undefined ? selectedItem.spoof_score.toFixed(4) : 'WAITING'}
                    </Badge>
                    <Badge colorScheme={selectedItem.verdict === 'spoof' ? 'red' : 'green'} fontSize="md" px={2}>
                      {selectedItem.verdict ? selectedItem.verdict.toUpperCase() : 'WAITING'}
                    </Badge>
                  </HStack>
                  <Text fontSize="xs" color="gray.400" mt={1}>Model: {selectedItem.model_version || '-'}</Text>
                </Box>
              </SimpleGrid>
            )}
            
            <Divider mb={4} />

            {/* Interactive Image Opacity Sliding Display */}
            {imageLoading ? (
              <Flex justify="center" align="center" h="300px"><Spinner size="lg" /></Flex>
            ) : (
              <Box>
                <Text fontSize="xs" color="gray.500" mb={2} fontWeight="semibold">VISUALISASI SCENE & HEATMAP AUDIT</Text>
                <Box position="relative" w="100%" h="350px" bg="black" borderRadius="md" overflow="hidden" mb={4}>
                  {originalBlobUrl && (
                    <Image src={originalBlobUrl} alt="Raw Capture" w="100%" h="100%" objectFit="contain" />
                  )}
                  {heatmapBlobUrl && (
                    <Image
                      src={heatmapBlobUrl}
                      alt="Heatmap Overlay"
                      position="absolute"
                      top={0}
                      left={0}
                      w="100%"
                      h="100%"
                      objectFit="contain"
                      opacity={overlayOpacity}
                      pointerEvents="none"
                      mixBlendMode="screen"
                    />
                  )}
                </Box>

                {heatmapBlobUrl && (
                  <Box bg="gray.50" p={3} borderRadius="lg" border="1px" borderColor="gray.100">
                    <Flex justify="space-between" mb={2}>
                      <Text fontSize="xs" fontWeight="bold" color="gray.600">Kontrol Transparansi Heatmap ({Math.round(overlayOpacity * 100)}%)</Text>
                      <Text fontSize="xx-small" color="gray.400">Gunakan slider untuk melihat bevel / bayangan layar HP</Text>
                    </Flex>
                    <Slider min={0} max={1} step={0.01} value={overlayOpacity} onChange={(val) => setOverlayOpacity(val)}>
                      <SliderTrack><SliderFilledTrack bg="teal.400" /></SliderTrack>
                      <SliderThumb boxSize={4} />
                    </Slider>
                  </Box>
                )}
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default SaktiLivenessAuditPage;
